import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Alert,
    Box,
    CircularProgress,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { importEmployeeAccounting, AccountingImportRow } from '../dataProvider';

interface ImportAccountingDialogProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Parses the "Employee" sheet from an XLSX workbook and extracts columns B–V
 * (indices 1–21) starting from the second row (row index 1, skipping the header).
 *
 * Column mapping (0-based within B–V, i.e. sheet column index - 1):
 *   B=0  Department
 *   C=1  Full Name
 *   D=2  English Name
 *   E=3  Gender
 *   F=4  Email
 *   G=5  Address
 *   H=6  CMND/CCCD (id_number)
 *   I=7  ID Issue Date
 *   J=8  ID Issue Place
 *   K=9  Job Title VN  → title
 *   L=10 Job Title EN  → title_en
 *   M=11 Contract Type
 *   N=12 DOB
 *   O=13 Phone
 *   P=14 Join Date mm/dd/yy (unused)
 *   R=16 Join Date yyyy-mm-dd → join_date
 *   S=17 Official Contract Date
 *   T=18 Level
 *   U=19 Note
 *
 * @param workbook - The parsed XLSX workbook.
 * @returns Parsed rows, or `null` if the "Employee" sheet is not found.
 */
export function parseEmployeeSheet (workbook: XLSX.WorkBook): AccountingImportRow[] | null {
    const sheetName = workbook.SheetNames.find(
        (n) => n.trim().toLowerCase() === 'employee'
    );
    if (!sheetName) return null;

    const sheet = workbook.Sheets[sheetName];
    // Use header: 1 to get raw arrays; defval: '' to fill empty cells
    const rawRows: string[][] = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: '',
        raw: false,
    }) as string[][];

    const result: AccountingImportRow[] = [];

    // Row 0 is the header row; data starts at row 1
    for (let i = 1; i < rawRows.length; i++) {
        const r = rawRows[i];
        // Skip entirely empty rows
        if (!r || r.every((cell) => cell === '' || cell === undefined || cell === null)) {
            continue;
        }

        // Columns B–V are at 0-based indices 1–21
        const row: AccountingImportRow = {
            department:             String(r[1] ?? '').trim() || undefined,
            full_name:              String(r[2] ?? '').trim() || undefined,
            english_name:           String(r[3] ?? '').trim() || undefined,
            gender:                 String(r[4] ?? '').trim() || undefined,
            email:                  String(r[5] ?? '').trim() || undefined,
            address:                String(r[6] ?? '').trim() || undefined,
            id_number:              String(r[7] ?? '').trim() || undefined,
            id_issue_date:          String(r[8] ?? '').trim() || undefined,
            id_issue_place:         String(r[9] ?? '').trim() || undefined,
            title:                  String(r[10] ?? '').trim() || undefined,
            title_en:               String(r[11] ?? '').trim() || undefined,
            contract_type:          String(r[12] ?? '').trim() || undefined,
            dob:                    String(r[13] ?? '').trim() || undefined,
            phone:                  String(r[14] ?? '').trim() || undefined,
            // Skip r[15] (mm/dd/yy format), use r[16] for yyyy-mm-dd
            join_date:              String(r[16] ?? '').trim() || undefined,
            official_contract_date: String(r[17] ?? '').trim() || undefined,
            level:                  String(r[18] ?? '').trim() || undefined,
            note:                   String(r[19] ?? '').trim() || undefined,
        };

        result.push(row);
    }

    return result;
}

/**
 * Dialog component for uploading an XLSX file of employee accounting data for
 * import. The user selects a file, previews the parsed rows, then submits to
 * `POST /employees/import-accounting`.
 */
export const ImportAccountingDialog: React.FC<ImportAccountingDialogProps> = ({ open, onClose }) => {
    const notify = useNotify();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [rows, setRows] = useState<AccountingImportRow[] | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = evt.target?.result;
                const workbook = XLSX.read(data, { type: 'array' });
                const parsed = parseEmployeeSheet(workbook);
                if (parsed === null) {
                    setParseError('Could not find the "Employee" sheet in the workbook.');
                    setRows(null);
                } else if (parsed.length === 0) {
                    setParseError('The "Employee" sheet contains no data rows.');
                    setRows(null);
                } else {
                    setParseError(null);
                    setRows(parsed);
                }
            } catch (err: any) {
                setParseError(`Failed to parse XLSX file: ${err?.message ?? 'Unknown error'}`);
                setRows(null);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleClose = () => {
        setRows(null);
        setParseError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        onClose();
    };

    const handleSubmit = async () => {
        if (!rows || rows.length === 0) return;

        setLoading(true);
        try {
            const result = await importEmployeeAccounting(rows);
            const msg = `Import complete: ${result.imported} row(s) imported, ${result.skipped} skipped.`;
            notify(msg, { type: 'success' });
            if (result.warnings && result.warnings.length > 0) {
                result.warnings.forEach((w) => notify(w, { type: 'warning' }));
            }
            handleClose();
        } catch (error: any) {
            const errorMessage =
                error?.body?.message || error?.message || 'Import failed';
            notify(errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const canSubmit = rows !== null && rows.length > 0 && !loading;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Import Employee Accounting</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Upload the <strong>[HR] Employee Contact.xlsx</strong> file.
                        The <em>Employee</em> sheet (columns B–V) will be parsed and
                        upserted into the accounting table.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            aria-label="Upload XLSX file"
                        />
                    </Box>
                </Box>

                {parseError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {parseError}
                    </Alert>
                )}

                {rows && rows.length > 0 && (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Preview ({rows.length} row{rows.length !== 1 ? 's' : ''})
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Full Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Department</TableCell>
                                        <TableCell>Job Title (VN)</TableCell>
                                        <TableCell>Contract Type</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{row.full_name ?? ''}</TableCell>
                                            <TableCell>{row.email ?? ''}</TableCell>
                                            <TableCell>{row.department ?? ''}</TableCell>
                                            <TableCell>{row.title ?? ''}</TableCell>
                                            <TableCell>{row.contract_type ?? ''}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!canSubmit}
                    startIcon={loading ? <CircularProgress size={16} /> : undefined}
                >
                    {loading ? 'Importing…' : 'Import'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
